// Generic (non-hydraulic-physics) [CONTROLS] bookkeeping shared by both
// routing methods (routing.ts's KINWAVE/STEADY loop and
// dynamicWaveRouting.ts's DYNWAVE loop) — purely about resolving what a
// [CONTROLS] rule's action/premise text refers to, independent of either
// method's own hydraulic solve.

import { curveLookup, type RawCurve } from "../curves.ts";
import {
  parseControlRule,
  parseControlVariables,
  type ParsedControlRule,
  type RuleVariable,
} from "../controlRules/controlRuleParser.ts";
import { parseMathExpr, type MathExpr } from "../mathExpr.ts";
import { warningText } from "../engineWarnings.ts";
import {
  readControlExpressions,
  readControlRules,
  readControlVariables,
} from "../../model/inp/controlRules.ts";

interface ControlTimeseriesArray {
  times: Date[];
  values: number[];
}

interface ControlActionLookup {
  curveSettingAt(curveName: string, controlValue: number): number | undefined;
  tseriesSettingAt(tseriesName: string, time: Date): number | undefined;
}

/**
 * [CONTROLS]'s own CURVE <name>/TIMESERIES <name> action-value resolution —
 * the real engine's object name lookup is case-insensitive, and a real EPA
 * fixture depends on it: `gate_control_3.inp`'s [CONTROLS] text references
 * `CURVE GATE_RATE` while [CURVES] declares the curve as `Gate_Rate`. A
 * plain case-sensitive `Map.get` would silently drop that action every
 * step. The upper-cased index here is scoped to this lookup only —
 * `controlCurves`/`controlTimeseriesArrays` keep their real verbatim-case
 * keys for display elsewhere.
 *
 * `tseriesSettingAt` takes `time` as a parameter because both callers build
 * one `ControlActionLookup` per simulation, then pass the current step's
 * time in on every call.
 */
export function buildControlActionLookup(
  controlCurves: ReadonlyMap<string, RawCurve>,
  controlTimeseriesArrays: ReadonlyMap<string, ControlTimeseriesArray>,
  interpolateAt: (times: Date[], values: number[], t: Date) => number
): ControlActionLookup {
  const controlCurvesByUpperName = new Map<string, string>(
    [...controlCurves.keys()].map((name) => [name.toUpperCase(), name])
  );
  const controlTimeseriesByUpperName = new Map<string, string>(
    [...controlTimeseriesArrays.keys()].map((name) => [name.toUpperCase(), name])
  );
  return {
    curveSettingAt: (curveName, controlValue) => {
      const curve =
        controlCurves.get(curveName) ??
        controlCurves.get(controlCurvesByUpperName.get(curveName.toUpperCase()) ?? "");
      return curve === undefined ? undefined : curveLookup(curve, controlValue);
    },
    tseriesSettingAt: (tseriesName, time) => {
      const series =
        controlTimeseriesArrays.get(tseriesName) ??
        controlTimeseriesArrays.get(
          controlTimeseriesByUpperName.get(tseriesName.toUpperCase()) ?? ""
        );
      return series === undefined ? undefined : interpolateAt(series.times, series.values, time);
    },
  };
}

interface ParsedControlsConfig {
  namedControlVariables: Map<string, RuleVariable>;
  namedControlExpressions: Map<string, MathExpr>;
  parsedControlRules: ParsedControlRule[];
}

/**
 * [CONTROLS] VARIABLE/EXPRESSION declarations + RULE bodies, parsed once per
 * project and shared by both routing methods — parsing has no dependency
 * on routing method or hydraulic state.
 *
 * A bad EXPRESSION or RULE is dropped individually with its own warning
 * rather than failing the whole parse; if every declared rule with a real
 * body ends up dropped, one extra aggregate warning is pushed too so that
 * outcome isn't just N indistinguishable per-rule warnings.
 */
export function parseControlRulesConfig(
  raw: ReadonlyMap<string, string[][]>,
  warnings: Set<string>
): ParsedControlsConfig {
  const namedControlVariables = parseControlVariables(readControlVariables(raw));
  const namedControlExpressionNames = new Set(readControlExpressions(raw).map((d) => d.name));
  const namedControlExpressions = new Map<string, MathExpr>();
  for (const def of readControlExpressions(raw)) {
    try {
      namedControlExpressions.set(def.name, parseMathExpr(def.expr));
    } catch {
      warnings.add(
        warningText(
          "engineWarning.controlsExpressionInvalid",
          `[CONTROLS] EXPRESSION "${def.name}" could not be parsed and was dropped — every premise referencing it resolves as undefined.`,
          { name: def.name }
        )
      );
    }
  }
  const controlRuleRows = readControlRules(raw);
  const parsedControlRules: ParsedControlRule[] = [];
  for (const r of controlRuleRows) {
    const parsed = parseControlRule(r, namedControlVariables, namedControlExpressionNames);
    if (parsed !== null) {
      parsedControlRules.push(parsed);
    } else if (r.body.trim().length > 0) {
      warnings.add(
        warningText(
          "engineWarning.controlsRuleUnsupported",
          `[CONTROLS] rule "${r.name}" uses a form Phase 1 doesn't support and was dropped (see controlRuleParser.ts's own header for what's covered).`,
          { name: r.name }
        )
      );
    }
  }
  if (
    controlRuleRows.length > 0 &&
    parsedControlRules.length === 0 &&
    controlRuleRows.some((r) => r.body.trim().length > 0)
  ) {
    warnings.add(
      warningText(
        "engineWarning.controlsNoRulesEvaluated",
        "[CONTROLS] rules are present but none could be evaluated — every rule used a form Phase 1 doesn't support (see controlRuleParser.ts's own header for what's covered)."
      )
    );
  }
  return { namedControlVariables, namedControlExpressions, parsedControlRules };
}
